import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { InventoryItem } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const InventoryGaps: React.FC = () => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [hideMonitored, setHideMonitored] = useState(false);

  useEffect(() => {
    api
      .get<InventoryItem[]>('/inventory-gaps')
      .then(setItems)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const f = filter.toLowerCase();
    return items.filter((i) => {
      if (hideMonitored && i.detectorEnabled === true) return false;
      if (!f) return true;
      return (
        (i.logGroupName || '').toLowerCase().includes(f) ||
        (i.appId || '').toLowerCase().includes(f) ||
        (i.stackId || '').toLowerCase().includes(f) ||
        (i.accountId || '').toLowerCase().includes(f) ||
        (i.region || '').toLowerCase().includes(f)
      );
    });
  }, [items, filter, hideMonitored]);

  const unknownStacks = items.filter((i) => i.stackId === 'unknown' || !i.stackId).length;
  const unmonitored = items.filter((i) => i.detectorEnabled !== true).length;

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-4">
      <div>
        <h1>Inventory</h1>
        <p className="text-sm text-slate-500 mt-1">
          Log groups discovered across all configured apps and accounts.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-padded">
          <div className="label">Total log groups</div>
          <div className="text-2xl font-semibold text-slate-900">{items.length}</div>
        </div>
        <div className="card-padded">
          <div className="label">Distinct stacks</div>
          <div className="text-2xl font-semibold text-slate-900">
            {new Set(items.map((i) => i.stackId || 'unknown')).size}
          </div>
        </div>
        <div className="card-padded">
          <div className="label">Unresolved stack tag</div>
          <div className={`text-2xl font-semibold ${unknownStacks > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
            {unknownStacks}
          </div>
        </div>
        <div className="card-padded">
          <div className="label">Unmonitored log groups</div>
          <div className={`text-2xl font-semibold ${unmonitored > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            {unmonitored}
          </div>
        </div>
      </div>

      <div className="card-padded flex flex-wrap items-center gap-3">
        <input
          type="text"
          className="input flex-1 min-w-[16rem]"
          placeholder="Filter by name, app, stack, account, region…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
          <input
            type="checkbox"
            checked={hideMonitored}
            onChange={(e) => setHideMonitored(e.target.checked)}
          />
          Hide monitored
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="card-padded text-sm text-slate-500">No log groups match.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Log Group</th>
                <th>App</th>
                <th>Stack</th>
                <th>Account</th>
                <th>Region</th>
                <th>Monitored</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => (
                <tr key={item.logGroupArn || idx}>
                  <td className="font-mono text-xs">{item.logGroupName}</td>
                  <td>{item.appId || '—'}</td>
                  <td className={item.stackId === 'unknown' ? 'italic text-slate-500' : ''}>
                    {item.stackId || '—'}
                  </td>
                  <td className="font-mono text-xs text-slate-600">{item.accountId}</td>
                  <td className="text-slate-600">{item.region}</td>
                  <td>
                    <span className={item.detectorEnabled ? 'badge-ok' : 'badge-medium'}>
                      {item.detectorEnabled ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InventoryGaps;
